<template>
  <v-container>
    <v-row
      justify="space-between"
      align="center"
    >
      <v-col
        cols="12"
        class="text-center"
      >
        <h1 class="text-h1 text-uppercase font-weight-medium mb-6">
          Edit your order
        </h1>
        <v-divider />
      </v-col>
    </v-row>
    <v-row>
      <v-container>
        <loading-overlay
          :opacity="0.8"
          :is-loading="isLoading"
        />
        <stepper
          ref="stepper"
          data-test="flowStepper"
          :start-step="1"
          :steps="steps"
          :is-edit="true"
        >
          <v-stepper-content step="1">
            <v-row class="no-stepper-paddings">
              <v-col
                cols="12"
                md="6"
                class="pb-0"
              >
                <h4 class="text-h4 text-uppercase mb-10">
                  Edit your reference
                </h4>
                <p class="text-body-1 mb-4">
                  Please edit your room location to be used as a reference for
                  both your curtain and track order.
                </p>
                <ValidationObserver ref="observer">
                  <v-form
                    ref="form"
                    v-model="validLocationForm"
                  >
                    <ValidationProvider
                      v-slot="{ errors }"
                      rules="required"
                      name="room_location"
                    >
                      <v-text-field
                        v-model="orderRoom"
                        name="room location"
                        outlined
                        dense
                        solo
                        :error-messages="errors"
                      />
                    </ValidationProvider>
                  </v-form>
                </ValidationObserver>
              </v-col>

              <h1
                class="
                  no-stepper-paddings
                  pageTitle pageTitle_absolute
                  text-uppercase text-h3
                "
              >
                {{ $t('pages.curtainPage.steps.curtainDropEdit.title') }}
              </h1>

              <curtain-drop-picker
                is-edit
                :save-button-state="saveButtonState"
                @next="saveCurtain"
                @nextStep="next"
              />
            </v-row>
          </v-stepper-content>
          <v-stepper-content step="2">
            <v-row class="no-stepper-paddings">
              <h1
                class="
                  no-stepper-paddings
                  pageTitle pageTitle_absolute
                  text-uppercase text-h3
                "
              >
                {{ $t(`pages.curtainPage.steps.${railRod}-edit.title`) }}
              </h1>
              <curtain-flow
                ref="curtainFlow"
                is-edit
                :save-button-state="saveButtonState"
                @next="saveRailRod"
                @back="next"
                @previous="previous"
              />
            </v-row>
          </v-stepper-content>
        </stepper>
      </v-container>
    </v-row>
  </v-container>
</template>
<script>
import { CurtainFlowEditMixin } from '@/mixins/EditFlow'

const Stepper = () => import('@/components/Steppers/Stepper')
const CurtainFlow = () => import('@/components/Flows/CurtainFlow')
const CurtainDropPicker = () => import('@/components/Pickers/CurtainDropPicker')
import { FetchCurtain } from '@/mixins/Curtain/FetchCurtain'
import { FetchRailRod } from '@/mixins/Curtain/FetchRailRod'
import IframeIntegration from '@/mixins/IframeIntegration'
import {mapActions, mapState} from 'vuex'
import LoadingOverlay from '@/components/Overlays/LoadingOverlay'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {_} from "vue-underscore";

export default {
  name: 'CurtainConfigurator',
  components: {
    Stepper,
    CurtainFlow,
    CurtainDropPicker,
    LoadingOverlay,
    ValidationProvider,
    ValidationObserver
  },
  mixins: [FetchCurtain, FetchRailRod, IframeIntegration, CurtainFlowEditMixin],
  metaInfo() {
    return {
      title: `Edit Curtain Order #${this.articleOrderId}| Spotlight Vadain`
    }
  },
  data() {
    return {
      flowData: {},
      validLocationForm: null,
      saveButtonState:'default'
    }
  },
  computed: {
    railRod() {
      return this.$store.getters['order/getRailRodType']
    },
    steps() {
      return [
        this.$i18n.t('pages.curtainPage.steps.railRod.header'),
        this.$i18n.t(`pages.curtainPage.steps.curtainDrop.header`)
      ]
    },
    orderRoom: {
      set(val) {
        this.$store.commit('order/setRoom', val)
      },
      get() {
        return this.$store.getters['order/getRoom']
      }
    },
    ...mapState('order', ['curtainParameters', 'railRodParameters'])
  },
  async mounted() {
    this.$wait.start('CURTAIN_DATA_LOAD_WAITER')
    await this.resetState()
    await this.$store.dispatch(`fabrics/loadFabrics`, {
       page: 1, page_size: 4
    })
  },
  methods: {
    ...mapActions({
      updateCurtainOrder: 'order/updateCurtainOrder',
      updateRailRodOrder: 'order/updateRailRodOrder'
    }),
    prepareCurtainInfo(curtainApiData) {
      const preparedInfo = this.prepareApiInfo(curtainApiData)
      preparedInfo['split_type'] = preparedInfo.style
      if (preparedInfo.eyelet_color) {
        preparedInfo['ring_type'] = preparedInfo.eyelet_color
      }
      preparedInfo['main_fabric'] = preparedInfo.main_fabric_uuid
      return _.omit(preparedInfo, ['accessory_order'])
    },
    changeButtonState(state) {
      this.saveButtonState = state
      setTimeout(() => (this.saveButtonState = 'default'), 3000)
    },
    async saveCurtain() {
      this.$wait.start('isArticleUpdating')
      if (await this.updateCurtainOrder()) {
        this.changeButtonState('success')
        this.sendMessageToSpotlight('editCurtain', {
          articles: [{ orderNumber: this.curtainParameters.order_number }],
        })
      } else {
        this.changeButtonState('error')
      }
      this.$wait.end('isArticleUpdating')
    },
    async saveRailRod() {
      this.$wait.start('isArticleUpdating')
      if (await this.updateCurtainOrder() && await this.updateRailRodOrder()) {
        this.changeButtonState('success')
        this.sendMessageToSpotlight('editCurtain', {
          articles: [{ orderNumber: this.curtainParameters.order_number }],
        })
      } else {
        this.changeButtonState('error')
      }
      this.$wait.end('isArticleUpdating')
    },
    next() {
      this.$refs.stepper.next()
      this.$refs.stepper.scrollToTop()
    },
    activateStep(step) {
      this.$refs.stepper.setStep(parseInt(step))
    },
    previous() {
      this.$refs.stepper.previous()
      this.$refs.stepper.scrollToTop()
    }
  }
}
</script>
<style lang="scss" scoped>
.no-stepper-paddings {
  padding-top: 120px;
}
</style>
